<script>
let youtubeApiPromise;

function loadYouTubeApi() {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (youtubeApiPromise) return youtubeApiPromise;

  youtubeApiPromise = new Promise((resolve, reject) => {
    const previousReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      try {
        previousReady?.();
      } finally {
        resolve(window.YT);
      }
    };

    const script = document.querySelector('script[src="https://www.youtube.com/iframe_api"]')
      || document.createElement('script');
    script.addEventListener('error', () => {
      youtubeApiPromise = null;
      window.onYouTubeIframeAPIReady = previousReady;
      script.remove();
      reject(new Error('Unable to load the YouTube player API.'));
    }, { once: true });

    if (!script.src) {
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      document.head.appendChild(script);
    }
  });

  return youtubeApiPromise;
}

export default {
  name: 'YouTubeEmbed',

  props: {
    src: {
      type: String,
      required: true,
    },

    muted: {
      type: Boolean,
      default: true,
    },

    loop: {
      type: Boolean,
      default: true,
    },

    autoplay: {
      type: Boolean,
      default: true,
    },

    videoPlaying: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'error',
    'durationchange',
    'ready',
    'play',
    'pause',
    'state-change',
    'ended',
    'timeupdate',
  ],

  data() {
    return {
      player: null,
      network: 'youtube',
      paused: true,
      timerInterval: null,
    };
  },

  computed: {
    duration() {
      return this.player?.getDuration();
    },

    currentTime: {
      get() {
        return this.player?.getCurrentTime();
      },
      set(v) {
        this.player?.seekTo(v);
      },
    },
  },

  watch: {
    videoPlaying(isPlaying) {
      if (isPlaying) {
        this.play();
      } else {
        this.pause();
      }
    },

    muted(newValue) {
      if (newValue) {
        this.mute();
      } else {
        this.unmute();
      }
    },
  },

  mounted() {
    this.initYouTube();
  },

  beforeUnmount() {
    clearInterval(this.timerInterval);
    this.player?.destroy?.();
  },

  methods: {
    getDuration() {
      return this.duration;
    },

    startTimerInterval() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
      }

      this.timerInterval = setInterval(() => {
        if (!this.paused) {
          this.$emit('timeupdate', {
            network: this.network,
            target: this.player,
          });
        }
      }, 250);
    },

    play() {
      this.player?.playVideo();
    },

    pause() {
      this.player?.pauseVideo();
    },

    whenPlay(event) {
      this.paused = false;
      this.$emit('play', { network: this.network, target: event.target });
      this.startTimerInterval();
    },

    whenPause(event) {
      this.paused = true;
      this.$emit('pause', { network: this.network, target: event.target });
      clearInterval(this.timerInterval);
    },

    mute() {
      this.player?.mute();
    },

    unmute() {
      this.player?.unMute();
    },

    async initYouTube() {
      const videoId = this.src.split('v=').pop();

      try {
        const youtube = await loadYouTubeApi();
        if (!this.$refs.player) return;

        this.player = new youtube.Player(this.$refs.player, {
          videoId,
          width: '100%',
          height: '100%',
          playerVars: {
            autoplay: this.autoplay ? 1 : 0,
            loop: this.loop ? 1 : 0,
            controls: 0,
          },
          events: {
            onReady: this.onYouTubePlayerReady,
            onStateChange: this.onYouTubePlayerStateChange,
          },
        });
      } catch (error) {
        this.$emit('error', { message: error.message });
      }
    },

    onYouTubePlayerReady(event) {
      this.$emit('ready', { network: this.network, target: event.target });
      this.$emit('durationchange', {
        network: this.network,
        target: event.target,
      });

      event.target.seekTo(0);

      if (this.muted && !event.target.isMuted()) {
        event.target.mute();
      } else if (!this.muted && event.target.isMuted()) {
        event.target.unMute();
      }

      if (this.autoplay) {
        event.target.playVideo();
        this.paused = false;
        this.startTimerInterval();
        this.$emit('play', { network: this.network, target: event.target });
      } else {
        event.target.stopVideo();
        this.paused = true;
        this.$emit('pause', { network: this.network, target: event.target });
      }
    },

    onYouTubePlayerStateChange(event) {
      this.$emit('state-change', {
        network: this.network,
        target: event.target,
      });

      switch (event.data) {
        case YT.PlayerState.ENDED: {
          if (this.loop) {
            event.target.seekTo(0);
            this.play();
          } else {
            clearInterval(this.timerInterval);
            this.$emit('ended', {
              network: this.network,
              target: event.target,
            });
          }
          break;
        }

        case YT.PlayerState.PAUSED: {
          this.whenPause(event);
          break;
        }

        case YT.PlayerState.PLAYING: {
          this.whenPlay(event);
          break;
        }

        default:
          break;
      }
    },
  },
};
</script>

<template>
  <div class="youtube-embed">
    <div ref="player" />
  </div>
</template>

<style scoped>
.youtube-embed {
  width: 100%;
  height: 100%;
}

.tgb-iframe--youtube {
  width: inherit;
  height: inherit;
  border: none;
  margin: 0;
  padding: 0;
  border: 0;
  font-size: 100%;
  font: inherit;
  vertical-align: baseline;
}
</style>
